/**
 * useMyOrders — 客户只读订单、付款与项目解锁视图 hook（Issue #214）。
 *
 * 与 useServiceStages、useMyHealthReports 同样的契约：
 *   - 仅访问 /api/v1/me/orders；身份完全来自 access token，
 *     客户端不传 customerId / tenantId / orgId。
 *   - 401 自动尝试一次 refresh，再失败由 UI 折射成 session_expired。
 *   - 404 视为"客户档案尚未建立"，单独区分以便文案克制。
 *
 * 红线：
 *   - 不展示 tenantId / orgId / costPrice / 内部 operator ID 等敏感字段。
 *   - 金额、比例只接收 decimal 字符串；任何格式化交给 utils/money.ts。
 *   - 不在本地拼装假账单数据；UI 完全信任后端响应。
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@jinzhuan/api-client';
import type { ApiComponents } from '@jinzhuan/api-client';
import {
  createCustomerMeApiClient,
  refreshCustomerSession,
} from './customerSession';
import { classifyMeFetchError, type MeFetchError } from './classify';

export type MeOrdersDto = ApiComponents['schemas']['MeOrdersDto'];
export type MeOrderDto = ApiComponents['schemas']['MeOrderDto'];
export type MeOrderProjectDto = ApiComponents['schemas']['MeOrderProjectDto'];
export type MeOrderServiceUserDto =
  ApiComponents['schemas']['MeOrderServiceUserDto'];
export type MeOrderProjectPaymentSummaryDto =
  ApiComponents['schemas']['MeOrderProjectPaymentSummaryDto'];
export type MeOrderProjectUnlockDto =
  ApiComponents['schemas']['MeOrderProjectUnlockDto'];

export interface UseMyOrdersResult {
  data: MeOrdersDto | null;
  loading: boolean;
  notFound: boolean;
  error: MeFetchError | null;
  refetch: () => void;
}

export function useMyOrders(): UseMyOrdersResult {
  const [data, setData] = useState<MeOrdersDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<MeFetchError | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setNotFound(false);
      setError(null);

      try {
        const apiClient = createCustomerMeApiClient();
        const dto = (await apiClient.GET('/api/v1/me/orders')).data ?? null;
        if (cancelled) return;
        setData(dto);
      } catch (err: unknown) {
        if (cancelled) return;
        const classified = classifyMeFetchError(err);
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        if (classified.kind === 'session_expired') {
          const refreshed = await refreshCustomerSession();
          if (cancelled) return;
          if (refreshed) {
            try {
              const retryDto =
                (await createCustomerMeApiClient().GET('/api/v1/me/orders'))
                  .data ?? null;
              setData(retryDto);
              setError(null);
              return;
            } catch (retryErr: unknown) {
              if (retryErr instanceof ApiError && retryErr.status === 404) {
                setNotFound(true);
                return;
              }
              setError(classified);
              return;
            }
          }
        }
        setError(classified);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { data, loading, notFound, error, refetch };
}

/**
 * Customer-facing label for a sales stage. Mirrors the wording in
 * `/me/service-stages`. UI components should not interpret raw enum values.
 */
export function formatSalesStage(stage: MeOrderDto['salesStage']): string {
  switch (stage) {
    case 'lead_in':
      return '线索接洽';
    case 'link_building':
      return '建立联系';
    case 'trust_building':
      return '信任沟通';
    case 'needs_discovery':
      return '需求确认';
    case 'proposal_quote':
      return '方案与报价';
    case 'marketing':
      return '签约付款';
    case 'delivering':
      return '项目交付';
    case 'maintaining':
      return '随访维护';
    case 'repurchased':
      return '已复购';
    case 'referred':
      return '已转介绍';
    case 'lost':
      return '订单已结束';
    default:
      return '进行中';
  }
}

/**
 * Customer-facing label for a project status. Internal lifecycle values
 * such as `cancelled` are mapped to user-safe wording.
 */
export function formatProjectStatus(status: string): string {
  switch (status) {
    case 'pending':
      return '等待启动';
    case 'in_progress':
      return '进行中';
    case 'paused':
      return '暂停中';
    case 'completed':
      return '已完成';
    case 'cancelled':
      return '已取消';
    default:
      return status;
  }
}

/**
 * Customer-facing label for an unlock status.
 */
export function formatUnlockStatus(
  unlock: MeOrderProjectUnlockDto | null | undefined,
): { label: string; tone: 'good' | 'progress' | 'warn' | 'neutral' } {
  if (!unlock) return { label: '尚未生成', tone: 'neutral' };
  switch (unlock.status) {
    case 'unlocked':
      return { label: '已解锁', tone: 'good' };
    case 'locked':
      return { label: '待解锁', tone: 'progress' };
    case 'expired':
      return { label: '已过期', tone: 'warn' };
    default:
      return { label: '状态未知', tone: 'neutral' };
  }
}
